import React, { Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import container from "../../../../styles/Global.module.css";
import {
  socialMediaOtherStatus,
  socialMediaStatus,
} from "../../../../json/StatusOptions.js";
import ColumnStatus from "../../../../components/global/fields/ColumnStatus.jsx";
import UsersSelect from "../../../../components/global/fields/UsersSelect.jsx";
import EditableText from "../../../../components/global/fields/EditableText.jsx";
import { getStatus } from "../../../../utils/ApiCall.js";
import BaseBoard from "../../../global/BoardTemplate/BaseBoard.jsx";
import { GridLogicOperator } from "@mui/x-data-grid-pro";
import { useCustomState } from "../../../../utils/useCustomState.js";
import ThreadListModal from "../../../Threads/components/ThreadListModal.jsx";
import SingleDuration from "../../../../components/global/fields/SingleDurationDatePicker.jsx";
import ReporteInvalidaciones from "../../../../components/global/fields/InvalidationReportUploader.jsx";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const AlunaSocialMedia = () => {
  const location = useLocation();
  let pathname = location.pathname;
  const apiUrl = `${backendUrl}/api/social-media-aluna/items`;
  const apiUsers = `${backendUrl}/api/users`;

  const [state, updateState] = useCustomState({
    status: "Open",
    groups: ["RRSS"],
    users: [],
    items: [],
    itemsDone: [],
    selectionRow: [],
    updateRows: [],
    columnsFilter: { id: false },
    viewFilter: {
      items: [],
      quickFilterLogicOperator: GridLogicOperator.Or,
    },
    pinnedColumns: {
      left: ["__check__", "threads"],
      right: [],
    },
    refreshState: false,
  });

  useEffect(() => {
    const fetchApi = async () => {
      const resUsers = await fetch(apiUsers);
      const data = await resUsers.json();
      updateState({ users: data });
    };
    fetchApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortItemsByDate = (items) => {
    return items?.sort((a, b) => {
      const dateA = new Date(a.publishedAt || a.createdAt || 0);
      const dateB = new Date(b.publishedAt || b.createdAt || 0);
      return dateB - dateA; // Orden descendente (más recientes primero)
    });
  };

  const filterItemsByStatus = (items, status, condition) => {
    let filteredItems;
    if (condition) {
      filteredItems = items?.filter((item) => item.status === status);
    } else {
      filteredItems = items?.filter((item) => item.status !== status);
    }

    // Ordenar los items filtrados por fecha
    return sortItemsByDate(filteredItems);
  };

  const filterItemsAndSetGroups = (items) => {
    updateState({
      itemsDone: filterItemsByStatus(items, "Done", true),
      items: filterItemsByStatus(items, "Done", false),
    });
  };

  const handleEditResponseChange = async (newValue, rowID, field) => {
    let status;
    if (field !== "status") {
      status = await getStatus(rowID, `social-media-aluna`);
    } else {
      status = newValue;
    }

    if (status === "Done") {
      const updatedRowsTerminados = state.itemsDone.map((row) => {
        if (+row.id === +rowID) {
          return { ...row, [`${field}`]: newValue };
        }
        return row;
      });
      // Reordenar después de la actualización
      updateState({ itemsDone: sortItemsByDate(updatedRowsTerminados) });
    } else {
      const updatedRowsNoTerminados = state.items.map((row) => {
        if (+row.id === +rowID) {
          return { ...row, [`${field}`]: newValue };
        }
        return row;
      });
      // Reordenar después de la actualización
      updateState({ items: sortItemsByDate(updatedRowsNoTerminados) });
    }
  };

  const updateBoard = (newData) => {
    const updatedRows = [...state.updateRows, newData];
    updateState({ updateRows: updatedRows });
  };

  const getGroupRows = (groupName) => {
    return groupName === "RRSS" ? state.items : state.itemsDone;
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 70,
      headerAlign: "center",
      align: "center",
    },

    {
      field: "name",
      headerName: "Elemento",
      width: 300,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <EditableText
          {...params}
          field={"name"}
          handleEditResponseChange={handleEditResponseChange}
          initialValue={params.row.name}
        />
      ),
    },
    {
      field: "threads",
      headerName: "Thread",
      width: 70,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return <ThreadListModal {...params} />;
      },
    },

    {
      field: "responsibles",
      headerName: "Owner",
      width: 140,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <UsersSelect
          {...params}
          users={state.users}
          field={"responsibles"}
          handleEditResponseChange={handleEditResponseChange}
        />
      ),
    },
    {
      field: "image",
      headerName: "Imagen",
      width: 140,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Suspense fallback={<p>Cargando</p>}>
          <ReporteInvalidaciones
            {...params}
            backendUrl={backendUrl}
            initialValue={params?.row?.image}
            pathname={pathname}
            typeColumn={"postColumnMedia"}
            field={"image"}
            deleteTypeColumn={"deleteColumnMedia"}
            fileCount={params?.row?.image?.length || 0}
          />
        </Suspense>
      ),
    },
    {
      field: "deadline",
      headerName: "Deadline",
      width: 160,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <SingleDuration
          {...params}
          selectionRow={state.selectionRow}
          fieldKey="deadline"
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 160,
      cellClassName: "no-padding",
      headerAlign: "center",
      renderCell: (params) => (
        <ColumnStatus
          {...params}
          selectionRow={state.selectionRow}
          field={"status"}
          columnPosition={280}
          handleEditResponseChange={handleEditResponseChange}
          statusOption={socialMediaStatus}
        />
      ),
    },
    {
      field: "instagramPostingStatus",
      headerName: "Instagram Posting Status",
      width: 160,
      cellClassName: "no-padding",
      headerAlign: "center",
      renderCell: (params) => (
        <ColumnStatus
          {...params}
          selectionRow={state.selectionRow}
          field={"instagramPostingStatus"}
          columnPosition={280}
          handleEditResponseChange={handleEditResponseChange}
          statusOption={socialMediaOtherStatus}
        />
      ),
    },
    {
      field: "facebookPostingStatus",
      headerName: "Facebook Posting Status",
      width: 160,
      cellClassName: "no-padding",
      headerAlign: "center",
      renderCell: (params) => (
        <ColumnStatus
          {...params}
          selectionRow={state.selectionRow}
          field={"facebookPostingStatus"}
          columnPosition={280}
          handleEditResponseChange={handleEditResponseChange}
          statusOption={socialMediaOtherStatus}
        />
      ),
    },
    {
      field: "linkedInPostingStatus",
      headerName: "LinkedIn Posting Status",
      width: 160,
      cellClassName: "no-padding",
      headerAlign: "center",
      renderCell: (params) => (
        <ColumnStatus
          {...params}
          selectionRow={state.selectionRow}
          field={"linkedInPostingStatus"}
          columnPosition={280}
          handleEditResponseChange={handleEditResponseChange}
          statusOption={socialMediaOtherStatus}
        />
      ),
    },
  ];

  // Only render BaseBoard when apiUrl is set
  if (!apiUrl) {
    return <div className={container.globalContainer}>Cargando...</div>;
  }

  return (
    <div className={container.globalContainer}>
      <BaseBoard
        columns={columns}
        groups={state.groups}
        getGroupRows={getGroupRows}
        setGroupItems={filterItemsAndSetGroups}
        apiUrl={apiUrl}
        status={state.status}
        setStatus={(status) => updateState({ status })}
        updateBoard={updateBoard}
        refreshState={state.refreshState}
        updateRows={state.updateRows}
        setSelectionRow={(selectionRow) => updateState({ selectionRow })}
        selectionRow={state.selectionRow}
        viewFilter={state.viewFilter}
        setViewFilter={(viewFilter) => updateState({ viewFilter })}
        columnsFilter={state.columnsFilter}
        setColumnsFilter={(columnsFilter) => updateState({ columnsFilter })}
        pinnedColumns={state.pinnedColumns}
        setPinnedColumns={(pinnedColumns) => updateState({ pinnedColumns })}
        checkboxSelection={true}
        height={"80vh"}
        maxHeight={"80vh"}
      />
    </div>
  );
};

export default AlunaSocialMedia;
